/** Imports */
import {z} from 'zod';
import {zMedia} from '@/mongoSchemas/Post/Medias.zod';
import {zMongoObject, zMongoObjectId} from '@/types/utils';
import {zTikTokCreatorInfo} from '@/mongoSchemas/TikTokCreatorInfo.zod';
import {zTikTokAnchorEligibility} from '@/mongoSchemas/TikTokAnchorEligibility.zod';
import {zOrganization} from '@/mongoSchemas/Organization/Organizations.zod';


/** Types */
export type MetaAccessTokenDebug = z.infer<typeof zMetaAccessTokenDebug>;
export type AccessToken = z.infer<typeof zIAccessTokenSchema>;
export type Page = z.infer<typeof zPage>;
export type Platform = z.infer<typeof zPlatform>;


/// Zod schemas
export const zPlatform = z.enum([
    'facebook',
    'instagram',
    'tiktok',
    'youtube',
    'threads',
    'twitter',
]);
export const zMetaAccessTokenDebug = z.object({
    app_id: z.string().optional(),
    type: z.string(),
    application: z.string(),
    data_access_expires_at: z.number(),
    expires_at: z.number(),
    is_valid: z.boolean(),
    issued_at: z.number(),
    profile_id: z.string().optional(),
    scopes: z.string().array(),
    granular_scopes: z.object({
        scope: z.string(),
    }).array().optional(),
    user_id: z.string(),
    updatedAt: z.coerce.date().optional(),
});
const zIAccessTokenSchema = z.object({
    accessToken: z.string(),
    secret: z.string().optional(),
    scopes: z.string().array().optional(),
    expiresAt: z.instanceof(Date).optional(),
    dataAccessExpiresAt: z.instanceof(Date).optional(),
    debug: zMetaAccessTokenDebug.optional(),
});
const zFacebookPageInsightsSchema = z.object({
    followerCount: z.number(),
    reactions: z.record(z.string(), z.number()),
    totalInteractions: z.number(),
    impressions: z.number(),
    reach: z.number(),
    videoViews: z.number(),
    commentCount: z.number(),
    totalShares: z.number(),
    postCount: z.number(),
    externalPostCount: z.number(),
    totalPostCount: z.number(),
    monetizationApproximateEarnings: z.number().optional(),
    viewsByFollowerType: z.object({
        followers: z.number().int().nonnegative(),
        nonFollowers: z.number().int().nonnegative(),
        unknown: z.number().int().nonnegative().optional(),
    }).optional(),
});
const zInstagramPageInsightsSchema = z.object({
    followerCount: z.number(),
    reach: z.number(),
    impressions: z.number(),
    totalInteractions: z.number(),
    totalLikes: z.number(),
    totalComments: z.number(),
    totalSaved: z.number(),
    totalShares: z.number(),
    postCount: z.number(),
    externalPostCount: z.number(),
    totalPostCount: z.number(),
    viewsByFollowerType: z.object({
        followers: z.number().int().nonnegative(),
        nonFollowers: z.number().int().nonnegative(),
        unknown: z.number().int().nonnegative().optional(),
    }).optional(),
});
const zTikTokPageInsightsSchema = z.object({
    followerCount: z.number(),
    impressions: z.number(),
    totalInteractionsLifetime: z.number(),
    totalLikes: z.number(),
    totalComments: z.number(),
    totalShares: z.number(),
    postCount: z.number(),
    externalPostCount: z.number(),
    totalPostCount: z.number(),
});
const zYoutubePageInsightsSchema = z.object({
    subscriberCount: z.number(),
    viewCount: z.number(),
    likeCount: z.number(),
    dislikeCount: z.number(),
    commentCount: z.number(),
    shareCount: z.number(),
    totalInteractions: z.number(),
    videoCount: z.number(),
    externalVideoCount: z.number(),
    totalVideoCount: z.number(),
    monetizationApproximateEarnings: z.number().optional(),
    thumbnailImpressions: z.number().nonnegative().optional(),
    thumbnailImpressionsCtr: z.number().nonnegative().optional(),
    engagedViews: z.number().nonnegative().optional(),
    watchTimeMinutes: z.number().nonnegative().optional(),
    averagePercentageViewed: z.number().nonnegative().optional(),
    subscribersGained: z.number().nonnegative().optional(),
    subscribersLost: z.number().nonnegative().optional(),
    trafficSources: z.array(z.object({
        source: z.string(),
        engagedViews: z.number().nonnegative(),
        views: z.number().nonnegative(),
        watchTimeMinutes: z.number().nonnegative(),
    })).optional(),
    endScreenImpressions: z.number().nonnegative().optional(),
    endScreenClicks: z.number().nonnegative().optional(),
    endScreenClickRate: z.number().nonnegative().optional(),
    viewsByFollowerType: z.object({
        followers: z.number().int().nonnegative(),
        nonFollowers: z.number().int().nonnegative(),
        unknown: z.number().int().nonnegative().optional(),
    }).optional(),
});
const zThreadsPageInsightsSchema = z.object({
    followerCount: z.number(),
    views: z.number(),
    likes: z.number(),
    replies: z.number(),
    reposts: z.number(),
    quotes: z.number(),
    clicks: z.number(),
    totalInteractions: z.number(),
    demographicsCountry: z.record(z.string(), z.number()),
    demographicsGender: z.record(z.string(), z.number()),
    demographicsAge: z.record(z.string(), z.number()),
    demographicsCity: z.record(z.string(), z.number()),
    postCount: z.number(),
    externalPostCount: z.number(),
    totalPostCount: z.number(),
});
const zTwitterPageInsightsSchema = z.object({
    followerCount: z.number(),
    followingCount: z.number(),
    tweetCount: z.number(),
    externalTweetCount: z.number(),
    totalTweetCount: z.number(),
    listedCount: z.number(),
    likeCount: z.number(),
    replyCount: z.number(),
    quoteCount: z.number(),
    repostCount: z.number(),
    totalInteractions: z.number(),
    bookmarkCount: z.number(),
    impressions: z.number(),
});
export const zPage = zMongoObject.extend({

    // Main
    get organization() {
        return z.union([
            zMongoObjectId,
            zOrganization,
        ]);
    },
    socialMediaPlatform: zPlatform,
    pageName: z.string(),
    pageId: z.string(),
    pagePicture: z.string(),
    linkToProfile: z.string(),
    accessToken: zIAccessTokenSchema.optional(),
    refreshToken: z.string().optional(),
    pageIsPremium: z.boolean().optional(),

    // Parents
    parentPageId: z.string().optional(),
    parentPageName: z.string().optional(),
    parentPagePicture: z.string().optional(),

    // Data
    pageAdded: z.instanceof(Date).optional(),
    lastTimeAuthorized: z.instanceof(Date).optional(),
    lastTimeExternalPostsFetched: z.instanceof(Date).optional(),
    lastTimeInsightsFetched: z.instanceof(Date).optional(),
    tiktokCreatorInfo: zTikTokCreatorInfo.optional(),
    tiktokCreatorInfoUpdatedAt: z.instanceof(Date).optional(),
    tiktokAnchorEligibility: zTikTokAnchorEligibility.optional(),
    followerHistory: z.record(z.string(), z.number()),
    insights: z.record(
        z.string(),
        z.union([
            zFacebookPageInsightsSchema,
            zInstagramPageInsightsSchema,
            zTikTokPageInsightsSchema,
            zYoutubePageInsightsSchema,
            zThreadsPageInsightsSchema,
            zTwitterPageInsightsSchema,
        ]),
    ),

    // Options
    overlay: zMedia.optional(),
    brandingColor: z.string().optional(),

    // Flags
    hasError: z.boolean().optional(),
    hasSpamWarning: z.boolean().optional(),
    errorDescription: z.string().optional(),
    isDev: z.boolean().optional(),
    isPrivate: z.boolean().optional(),
});
